import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PlayerPublicTournaments from "../components/PlayerPublicTournaments";
import PlayerTournamentCard from "../components/PlayerTournamentCard";
import PlayerFamilyProfileSwitcher from "../components/PlayerFamilyProfileSwitcher";
import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type PlayerDashboardPayload,
  type PlayerDashboardTournamentCard,
} from "../lib/api";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";

const normalize = (value: string | null | undefined) =>
  (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const getTournamentLifecycle = (card: PlayerDashboardTournamentCard) => {
  const status = normalize(card.tournament_status);
  if (["finished", "completed", "terminated", "ended", "done", "closed", "archived", "finalizado", "terminado", "cerrado"].includes(status)) {
    return "finished";
  }
  return "active";
};

/** Año de inicio del torneo (o null sin fecha). */
const tournamentYear = (card: PlayerDashboardTournamentCard): string | null => {
  const match = card.tournament_start_date?.match(/^(\d{4})/);
  return match ? match[1] : null;
};

/** Más reciente primero: por fecha de inicio y, sin ella o empatados, por id. */
const byMostRecent = (a: PlayerDashboardTournamentCard, b: PlayerDashboardTournamentCard) => {
  const aDate = a.tournament_start_date ?? "";
  const bDate = b.tournament_start_date ?? "";
  if (aDate !== bDate) return bDate.localeCompare(aDate);
  return b.tournament_id - a.tournament_id;
};

export default function PlayerTournamentsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<PlayerDashboardPayload | null>(null);
  const scope = usePlayerProfileScope();
  const activeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameQuery, setNameQuery] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  // «Torneos públicos» (Eduardo 2026-09-26): segunda vista de esta página,
  // enlazable con ?vista=publicos.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("vista") === "publicos" ? "public" : "mine";
  const setView = (next: "mine" | "public") => {
    const params = new URLSearchParams(searchParams);
    if (next === "public") params.set("vista", "publicos");
    else params.delete("vista");
    setSearchParams(params, { replace: true });
  };

  const load = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.dashboards.player(token, activeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar tus torneos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [activeProfileId, latestLoad, scope.ready, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const tournaments = data?.tournaments ?? [];
  const familyProfiles = scope.profiles;
  const selectedProfileId = data?.profile?.id ?? activeProfileId;

  const handleProfileChange = (profileId: number) => {
    scope.setProfileId(profileId);
    clearFilters();
  };

  // Eduardo 2026-10-06: solo búsqueda por nombre y año (sin Tipo/Estado).
  const tournamentYears = useMemo(
    () =>
      Array.from(
        new Set(tournaments.map(tournamentYear).filter((year): year is string => Boolean(year))),
      ).sort((a, b) => b.localeCompare(a)),
    [tournaments],
  );

  const filteredTournaments = useMemo(() => {
    const query = normalize(nameQuery);
    return tournaments.filter((card) => {
      const nameMatches =
        !query || normalize(card.tournament_name ?? `Torneo ${card.tournament_id}`).includes(query);
      const yearMatches = yearFilter === "all" || tournamentYear(card) === yearFilter;
      return nameMatches && yearMatches;
    });
  }, [nameQuery, tournaments, yearFilter]);

  // Activos y próximos arriba; los terminados al final. Cada grupo, el más
  // reciente primero.
  const activeTournaments = useMemo(
    () => filteredTournaments.filter((card) => getTournamentLifecycle(card) === "active").sort(byMostRecent),
    [filteredTournaments],
  );
  const finishedTournaments = useMemo(
    () => filteredTournaments.filter((card) => getTournamentLifecycle(card) === "finished").sort(byMostRecent),
    [filteredTournaments],
  );

  const clearFilters = () => {
    setNameQuery("");
    setYearFilter("all");
  };

  return (
    <>
      {error && (
        <section className="card">
          <p role="alert" className="form-error">
            {error}
          </p>
        </section>
      )}

      <section className="card player-tournaments-browser">
        <div className="card__header-row player-tournaments-browser__header">
          <div>
            <span className="player-pro-eyebrow">{view === "public" ? "Torneos públicos" : "Mis torneos"}</span>
            <h1 className="card__title">Torneos</h1>
            <p className="card__subtitle">
              {view === "public"
                ? "Torneos con inscripciones abiertas en los que puedes inscribirte."
                : "Busca tus torneos por nombre o año."}
            </p>
          </div>
          <PlayerFamilyProfileSwitcher
            familyProfiles={familyProfiles}
            activeProfileId={selectedProfileId}
            onChange={handleProfileChange}
          />
        </div>

        <div className="player-tournaments-tabs" role="tablist" aria-label="Vista de torneos">
          <button
            type="button"
            role="tab"
            aria-selected={view === "mine"}
            className={view === "mine" ? "is-active" : ""}
            onClick={() => setView("mine")}
          >
            Mis torneos
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "public"}
            className={view === "public" ? "is-active" : ""}
            onClick={() => setView("public")}
          >
            Torneos públicos
          </button>
        </div>

        {view === "mine" ? (
        <div className="player-tournament-filters" aria-label="Filtros de torneos">
          <label>
            <span>Nombre</span>
            <input
              type="search"
              value={nameQuery}
              onChange={(event) => setNameQuery(event.target.value)}
              placeholder="Buscar torneo…"
            />
          </label>
          <label>
            <span>Año</span>
            <select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)}>
              <option value="all">Todos los años</option>
              {tournamentYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        ) : null}
      </section>

      {view === "public" ? (
        <section className="card">
          {/* Espera al perfil activo que resuelve el home (evita pedir dos veces). */}
          {loading && !data ? (
            <p className="muted">Cargando…</p>
          ) : token && data ? (
            <PlayerPublicTournaments token={token} profileId={selectedProfileId ?? null} />
          ) : null}
        </section>
      ) : (
      <section className="card">
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : !data?.viewer.linked_player ? (
          <p className="muted">
            Tu cuenta no está vinculada a un perfil de jugador. Pídele al admin que te genere un PIN.
          </p>
        ) : tournaments.length === 0 ? (
          <p className="muted">
            Aún no estás inscrito en ningún torneo. Cuando un admin te inscriba verás aquí tus torneos y partidos.
          </p>
        ) : filteredTournaments.length === 0 ? (
          <p className="muted">No encontramos torneos con esos filtros.</p>
        ) : (
          <>
            {activeTournaments.length > 0 ? (
              <div className="tournament-card-grid">
                {activeTournaments.map((card) => (
                  <PlayerTournamentCard key={card.tournament_id} card={card} />
                ))}
              </div>
            ) : null}
            {finishedTournaments.length > 0 ? (
              <>
                <h2 className="player-tournaments-finished-title">Torneos terminados</h2>
                <div className="tournament-card-grid">
                  {finishedTournaments.map((card) => (
                    <PlayerTournamentCard key={card.tournament_id} card={card} />
                  ))}
                </div>
              </>
            ) : null}
          </>
        )}
      </section>
      )}
    </>
  );
}
